// Supabase 連接測試工具
import supabase from '../config/supabase';

export const testSupabaseConnection = async () => {
  try {
    // 1. 測試基本連接
    const { error: connectionError } = await supabase
      .from('pets')
      .select('count')
      .limit(1);

    if (connectionError) {
      if (connectionError.message.includes('relation "pets" does not exist')) {
        return {
          connected: false,
          needsTableCreation: true,
          error: connectionError,
          recommendation: '請在 Supabase Dashboard 的 SQL Editor 中執行: src/supabase/migrations/1762093567265-create_pets_table.sql'
        };
      }
      return { connected: false, needsTableCreation: false, error: connectionError };
    }

    // 2. 測試認證狀態
    const { data: { user } } = await supabase.auth.getUser();

    // 3. 測試資料表結構
    const { data: tableTest, error: tableError } = await supabase
      .from('pets')
      .select('id, name, type, share_token, user_id, created_at')
      .limit(1);

    if (tableError) {
      return {
        connected: true,
        tableStructureOk: false,
        error: tableError,
        recommendation: '請檢查資料表結構是否正確'
      };
    }

    // 4. 測試 RLS 政策（僅在已登入時）
    if (user) {
      await supabase.from('pets').select('*').eq('user_id', user.id).limit(5);
    }

    // 5. 測試分享功能
    const { data: shareTest } = await supabase
      .from('pets')
      .select('id, name, share_token')
      .not('share_token', 'is', null)
      .limit(1);

    return {
      connected: true,
      tableStructureOk: true,
      petCount: tableTest?.length || 0,
      shareablePets: shareTest?.length || 0,
      user,
      allTestsPassed: true
    };
  } catch (err) {
    return {
      connected: false,
      error: err,
      recommendation: '請檢查網絡連接和 Supabase 配置'
    };
  }
};

export const createPetsTable = async () => ({
  success: false,
  message: '請手動在 Supabase Dashboard 中執行 SQL',
  instructions: `
請按照以下步驟在 Supabase Dashboard 中建立資料表:
1. 登入 Supabase Dashboard: https://supabase.com/dashboard
2. 選擇您的專案: ktqwxutgkbnygnzwqwar
3. 點擊左側選單的 "SQL Editor"
4. 建立新查詢
5. 複製並執行以下檔案內容:
   src/supabase/migrations/1762093567265-create_pets_table.sql
`,
});

// ✅ 模組層級：SQL 字串只建立一次
const MIGRATION_SQL = `-- PetCare 資料庫遷移 SQL
CREATE TABLE IF NOT EXISTS pets (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id uuid REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  name text NOT NULL,
  type text NOT NULL CHECK (type IN ('dog', 'cat', 'bird', 'fish', 'rabbit', 'hamster', 'turtle', 'other')),
  breed text, age numeric,
  gender text CHECK (gender IN ('male', 'female', '')),
  weight text, height text, color text, microchip_id text, registration_id text, photo_url text,
  feeding_schedule text, portion_size text, can_eat text, cannot_eat text,
  likes text, dislikes text, temperament text, exercise_needs text, training_notes text,
  medications text, medication_schedule text, vaccines text, next_vaccine_date date,
  allergies text, medical_conditions text, vet_name text, vet_phone text,
  insurance_provider text, insurance_policy text, special_notes text,
  share_token text UNIQUE, health_status text DEFAULT 'healthy',
  last_vet_visit date, next_appointment date, profile_completion integer DEFAULT 0,
  created_at timestamptz DEFAULT now(), updated_at timestamptz DEFAULT now()
);
ALTER TABLE pets ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Users can view own pets" ON pets FOR SELECT TO authenticated USING (auth.uid() = user_id);
CREATE POLICY "Users can insert own pets" ON pets FOR INSERT TO authenticated WITH CHECK (auth.uid() = user_id);
CREATE POLICY "Users can update own pets" ON pets FOR UPDATE TO authenticated USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
CREATE POLICY "Users can delete own pets" ON pets FOR DELETE TO authenticated USING (auth.uid() = user_id);
CREATE POLICY "Anyone can view pets via share token" ON pets FOR SELECT TO anon, authenticated USING (share_token IS NOT NULL);
CREATE INDEX IF NOT EXISTS pets_user_id_idx ON pets(user_id);
CREATE INDEX IF NOT EXISTS pets_share_token_idx ON pets(share_token);
CREATE INDEX IF NOT EXISTS pets_type_idx ON pets(type);
CREATE INDEX IF NOT EXISTS pets_created_at_idx ON pets(created_at DESC);
CREATE OR REPLACE FUNCTION update_updated_at_column() RETURNS TRIGGER AS $$
BEGIN NEW.updated_at = now(); RETURN NEW; END;
$$ language 'plpgsql';
CREATE TRIGGER update_pets_updated_at BEFORE UPDATE ON pets FOR EACH ROW EXECUTE FUNCTION update_updated_at_column();`;

export const showMigrationSQL = () => MIGRATION_SQL;

// 開發工具掛載（僅在瀏覽器環境）
if (typeof window !== 'undefined') {
  window.testSupabase = testSupabaseConnection;
  window.createPetsTable = createPetsTable;
  window.showMigrationSQL = showMigrationSQL;
}